import { useMemo } from "react";
import {
  MeetingChecklistDialog,
  buildInitialDialogState,
  type MeetingDialogState,
} from "@/components/project/MeetingChecklistDialog";
import ClosingChecklistDialog, { type ClosingStoragePayload } from "@/components/project/ClosingChecklistDialog";
import { ActivateProjectDialog } from "@/components/project/ActivateProjectDialog";
import ReopenProjectDialog from "@/components/project/ReopenProjectDialog";
import { toast } from "sonner";

import type { ChecklistItem, MeetingType } from "@/lib/db-types";
import type { ProjectClosingValidation } from "@/hooks/use-project-closing";
import {
  findMeeting,
  useProjectMeetings,
  useResetProjectMeeting,
  useUpsertProjectMeeting,
} from "@/hooks/use-project-meetings";
import { useChecklistTemplate } from "@/hooks/use-checklist-templates";

interface ProjectDialogsProps {
  showClosingDialog: boolean;
  onClosingDialogOpenChange: (open: boolean) => void;
  closingChecklist: ChecklistItem[];
  onClosingChecklistChange: (checklist: ChecklistItem[]) => void;
  onClosingConfirm: (storage?: ClosingStoragePayload) => void;
  closingValidation: ProjectClosingValidation;
  closingAdminOverride: boolean;
  /** VIB-1308: har prosjektet en kunde? Storage-avtale krever kunde. */
  closingHasCustomer?: boolean;
  projectId: string;

  showActivateDialog: boolean;
  onActivateDialogOpenChange: (open: boolean) => void;
  startupInvoicePct: number | null;
  totalAmount: number | null;
  onActivateConfirm: (createInvoice: boolean) => void;

  showStartupDialog: boolean;
  onStartupDialogOpenChange: (open: boolean) => void;

  showStatusDialog: boolean;
  onStatusDialogOpenChange: (open: boolean) => void;

  showDebriefDialog: boolean;
  onDebriefDialogOpenChange: (open: boolean) => void;

  showReopenDialog: boolean;
  onReopenDialogOpenChange: (open: boolean) => void;
  onReopenConfirm: () => void;
  projectName: string;
}

/** Dialog for oppstart eller debrief. Lagrer eksplisitt via Lagre-knapp. */
function MeetingDialogContainer({
  open,
  onOpenChange,
  projectId,
  meetingType,
  label,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  meetingType: MeetingType;
  label: string;
}) {
  const { data: meetings } = useProjectMeetings(projectId);
  const { data: template } = useChecklistTemplate(meetingType);
  const upsert = useUpsertProjectMeeting();
  const reset = useResetProjectMeeting();

  const dbMeeting = useMemo(() => findMeeting(meetings, meetingType), [meetings, meetingType]);
  const defaultItems = template?.items ?? [];

  const initialState = useMemo(
    () => buildInitialDialogState({ existing: dbMeeting, defaultItems }),
    [dbMeeting, defaultItems],
  );

  const handleSave = async (state: MeetingDialogState) => {
    try {
      await upsert.mutateAsync({
        projectId,
        meetingType,
        status: state.status,
        reason: state.reason,
        checklist: state.checklist,
        notes: state.notes,
        heldAt: state.heldAt,
        whatWentWell: state.whatWentWell ?? null,
        whatWentPoorly: state.whatWentPoorly ?? null,
        defaultItems,
      });
      toast.success(`${label} lagret`);
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Ukjent feil";
      toast.error(`Kunne ikke lagre: ${message}`);
    }
  };

  const handleReset = async () => {
    try {
      await reset.mutateAsync({ projectId, meetingType });
      toast.success(`${label} nullstilt`);
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Ukjent feil";
      toast.error(`Kunne ikke nullstille: ${message}`);
    }
  };

  return (
    <MeetingChecklistDialog
      open={open}
      onOpenChange={onOpenChange}
      label={label}
      initialState={initialState}
      defaultItems={defaultItems}
      showLearningFields={meetingType === "debrief"}
      onSave={handleSave}
      onReset={dbMeeting ? handleReset : undefined}
      saving={upsert.isPending}
      resetting={reset.isPending}
    />
  );
}

export function ProjectDialogs({
  showClosingDialog,
  onClosingDialogOpenChange,
  closingChecklist,
  onClosingChecklistChange,
  onClosingConfirm,
  closingValidation,
  closingAdminOverride,
  closingHasCustomer = false,
  projectId,
  showActivateDialog,
  onActivateDialogOpenChange,
  startupInvoicePct,
  totalAmount,
  onActivateConfirm,
  showStartupDialog,
  onStartupDialogOpenChange,
  showStatusDialog,
  onStatusDialogOpenChange,
  showDebriefDialog,
  onDebriefDialogOpenChange,
  showReopenDialog,
  onReopenDialogOpenChange,
  onReopenConfirm,
  projectName,
}: ProjectDialogsProps) {
  return (
    <>
      <ClosingChecklistDialog
        open={showClosingDialog}
        onOpenChange={onClosingDialogOpenChange}
        checklist={closingChecklist}
        onChecklistChange={onClosingChecklistChange}
        onConfirm={onClosingConfirm}
        projectId={projectId}
        validation={closingValidation}
        adminOverride={closingAdminOverride}
        hasCustomer={closingHasCustomer}
      />

      <ActivateProjectDialog
        open={showActivateDialog}
        onOpenChange={onActivateDialogOpenChange}
        startupInvoicePct={startupInvoicePct}
        totalAmount={totalAmount}
        onConfirm={onActivateConfirm}
      />

      <MeetingDialogContainer
        open={showStartupDialog}
        onOpenChange={onStartupDialogOpenChange}
        projectId={projectId}
        meetingType="startup"
        label="Oppstartsmøte"
      />

      <MeetingDialogContainer
        open={showStatusDialog}
        onOpenChange={onStatusDialogOpenChange}
        projectId={projectId}
        meetingType="status"
        label="Statusmøte"
      />

      <MeetingDialogContainer
        open={showDebriefDialog}
        onOpenChange={onDebriefDialogOpenChange}
        projectId={projectId}
        meetingType="debrief"
        label="Debrief"
      />

      <ReopenProjectDialog
        open={showReopenDialog}
        onOpenChange={onReopenDialogOpenChange}
        onConfirm={onReopenConfirm}
        projectName={projectName}
      />
    </>
  );
}
